/**
 * `GET`/`PUT /v1/me/view-prefs/:viewKey` — hva DENNE leseren vil se (QA13 runde 2, K-18 og J9).
 *
 * =================================================================================================
 * Atle, K-18: «Tvitvi husker brukerens visningsvalg. Hvilke arrangementer man vil se, filtre og
 * gruppering lagres per bruker på serveren, slik at det ikke må settes hver gang, og speiles i
 * URL-en for deling.»
 *
 * =================================================================================================
 * URL-EN VINNER NÅR DEN SIER NOE. Den er for DELING — «se dette» — og en lenke en kollega har sendt
 * skal vise det hen så, ikke det du så sist. Raden er for GJENOPPTAKELSE og svarer når adressen
 * ikke sier noe, altså når man åpner siden fra menyen.
 *
 * Rangeringen bor hos den som KALLER, ikke her: denne modulen leser og skriver, og siden avgjør
 * hva den gjør med svaret. Å legge sammenslåingen her ville bundet hver side til den samme formen
 * på adressen.
 *
 * =================================================================================================
 * SAMME FORM SOM `table-prefs.ts`, helt ned til `sendOrganizationHeader: false`: ruta er
 * `@OrgScope('none')`, fordi raden hører til mennesket. Huset ligger i NØKKELEN — `calendar:<id>`
 * — og ikke i en header.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { viewPrefsDtoSchema, type ViewPrefsDto } from '@tvitvi/shared';

import { apiRequest } from '@/api/client';
import { CACHE } from '@/api/cache-tiers';
import { qk } from '@/api/query-keys';

/** «calendar», «tasks» — navnet på visningen, uten huset. Se `viewKeyFor`. */
export type ViewName = 'calendar' | 'tasks' | 'festival';

/**
 * Nøkkelen, satt sammen av visningen og huset.
 *
 * Huset er med fordi hvilke arrangementer man vil se per definisjon er husets egne: den samme
 * personen i to hus har to svar. Kontrakten krever formen `<navn>:<uuid>`, så en nøkkel satt
 * sammen feil blir en 400 og ikke en rad ingen finner igjen.
 */
export function viewKeyFor(view: ViewName, organizationId: string): string {
  return `${view}:${organizationId}`;
}

export function viewPrefsQueryOptions(viewKey: string) {
  return queryOptions({
    queryKey: qk.viewPrefs(viewKey),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/me/view-prefs/${viewKey}`,
        schema: viewPrefsDtoSchema,
        signal,
        sendOrganizationHeader: false,
      }),
    ...CACHE.identity,
  });
}

export interface ViewPrefsHandle {
  /** Det lagrede valget, eller `{}` mens det hentes og når ingenting er lagret. */
  value: Record<string, unknown>;
  /** Om svaret har kommet. En side må ikke skrive over et valg den ikke har lest ennå. */
  ready: boolean;
  /** Skriv hele valget. Virker med en gang i cachen, lagres etterpå. */
  save: (next: Record<string, unknown>) => void;
  /** «Vis alt igjen» — sletter raden, så leseren følger med når standarden endres. */
  reset: () => void;
}

/**
 * Visningsvalget for én side.
 *
 * `ready` er ikke pynt. En side som skriver før den har lest ville lagret sin egen standard oppå
 * det leseren valgte i går — og det ville skjedd én gang per innlasting, altså alltid.
 */
export function useViewPrefs(viewKey: string, enabled = true): ViewPrefsHandle {
  const queryClient = useQueryClient();
  /* `enabled` (sak 217.13): skallet monteres også uten et aktivt hus, og da finnes ingen nøkkel. */
  const saved = useQuery({ ...viewPrefsQueryOptions(viewKey), enabled });

  const write = useMutation({
    mutationFn: (value: Record<string, unknown>) =>
      apiRequest({
        path: `/me/view-prefs/${viewKey}`,
        method: 'PUT',
        body: { value },
        schema: viewPrefsDtoSchema,
        sendOrganizationHeader: false,
      }),
    onMutate: async (value) => {
      await queryClient.cancelQueries({ queryKey: qk.viewPrefs(viewKey) });
      const previous = queryClient.getQueryData<ViewPrefsDto>(qk.viewPrefs(viewKey));
      queryClient.setQueryData<ViewPrefsDto>(qk.viewPrefs(viewKey), { viewKey, value });
      return { previous };
    },
    onError: (_error, _value, context) => {
      /* Mislykket skriving legger det gamle tilbake: en visning som lyver er verre enn en som
         glemmer. */
      if (context?.previous !== undefined) {
        queryClient.setQueryData(qk.viewPrefs(viewKey), context.previous);
      }
    },
  });

  /*
   * `write.mutate` er STABIL på tvers av renders i react-query v5; `write` selv er det ikke.
   * Forskjellen er ikke akademisk: kallstedene bruker `save` i en `useEffect`, og en ny identitet
   * hver render ville gjort den effekten til en løkke.
   */
  const mutate = write.mutate;

  const save = useCallback(
    (next: Record<string, unknown>) => {
      mutate(next);
    },
    [mutate],
  );

  const reset = useCallback(() => {
    mutate({});
  }, [mutate]);

  return {
    value: saved.data?.value ?? {},
    ready: saved.isSuccess,
    save,
    reset,
  };
}
